import { useEffect } from 'react'
import { ChevronDoubleLeftIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDoubleRightIcon } from "@heroicons/react/20/solid"

export default function PaginationController({ pagination, setPagination }) {

    const startItem = pagination.totalCount > 0 ? ((pagination.page - 1) * pagination.pageSize) + 1 : 0
    const endItem = Math.min(pagination.page * pagination.pageSize, pagination.totalCount)

    const OnPageChange = (newPage) => {
        setPagination(prev => {
            // Ensure page is within valid bounds
            const validPage = Math.max(1, Math.min(newPage, prev.totalPages || 1))
            return {
                ...prev,
                page: validPage,
                hasNext: validPage < (prev.totalPages || 1),
                hasPrevious: validPage > 1
            }
        })
    }

    // Handle keyboard navigation for pagination
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Only handle arrow keys if not typing in an input field
            if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
                return
            }

            if (event.key === 'ArrowLeft' && pagination.hasPrevious) {
                event.preventDefault()
                OnPageChange(pagination.page - 1)
            } else if (event.key === 'ArrowRight' && pagination.hasNext) {
                event.preventDefault()
                OnPageChange(pagination.page + 1)
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [pagination.hasPrevious, pagination.hasNext, pagination.page])

    return (
        <div className="pl-1 flex items-center justify-between">
            <div className="text-sm text-gray-700">
                Showing {startItem} to {endItem} of {pagination.totalCount} results
            </div>
            
            <div className="flex items-center gap-x-2">
                <button
                    onClick={() => OnPageChange(1)}
                    disabled={pagination.page === 1 || pagination.totalPages === 0}
                    className="bg-white font-medium pl-2 pr-3 py-1 text-sm border border-gray-200 rounded shadow-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-x-1"
                >
                    <ChevronDoubleLeftIcon className="h-4 w-4" />
                    First
                </button>

                <button
                    onClick={() => OnPageChange(pagination.page - 1)}
                    disabled={pagination.page === 1 || pagination.totalPages === 0}
                    className="bg-white font-medium pl-2 pr-3 py-1 text-sm border border-gray-200 rounded shadow-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-x-1"
                >
                    <ChevronLeftIcon className="h-4 w-4" />
                    Previous
                </button>
                
                <span className="px-2 py-1 text-sm text-gray-700">
                    Page {pagination.page} of {pagination.totalPages || 1}
                </span>
                
                <button
                    onClick={() => OnPageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages || pagination.totalPages === 0}
                    className="bg-white font-medium pl-3 pr-2 py-1 text-sm border border-gray-200 rounded shadow-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-x-1"
                >
                    Next
                    <ChevronRightIcon className="h-4 w-4" />
                </button>
                
                <button
                    onClick={() => OnPageChange(pagination.totalPages)}
                    disabled={pagination.page >= pagination.totalPages || pagination.totalPages === 0}
                    className="bg-white font-medium pl-3 pr-2 py-1 text-sm border border-gray-200 rounded shadow-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-x-1"
                >
                    Last
                    <ChevronDoubleRightIcon className="h-4 w-4" />
                </button>
            </div>
        </div>
    )
} 